import { useMemo } from "react"
import { motion } from "motion/react"
import {
	IconCheckOutline18 as Check,
	IconGlobeOutline18 as Globe,
} from "nucleo-ui-outline-18"

import { MAIN_PORTAL_INSTANCE_ID } from "@productlane/lib/src/portal-instance"
import { Button, DropdownMenu } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import { toast } from "@/components/toast"
import { analytics } from "@/lib/analytics"
import { useRole } from "@/lib/useRole"
import { mutateOk, useZero, useZeroWorkspace } from "@/lib/zero"
import {
	changelogPortalIds,
	portalSelectionLabel,
	togglePortalId,
} from "./changelog-portals"

interface Props {
	changelog: {
		id: string
		portalInstanceId?: string | null
		portalInstanceIds?: readonly string[] | null
	}
}

export const ChangelogPortalPicker = ({ changelog }: Props) => {
	const zero = useZero()
	const workspace = useZeroWorkspace()
	const { role } = useRole()
	const canEdit = role !== "VIEWER"

	const namedPortals = workspace?.portalInstances ?? []

	const portals = useMemo(
		() => [
			{ id: MAIN_PORTAL_INSTANCE_ID, name: "Main portal" },
			...namedPortals.map((portal: { id: string; name: string }) => ({
				id: portal.id,
				name: portal.name,
			})),
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[namedPortals],
	)

	const selected = useMemo(
		() => changelogPortalIds(changelog),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[changelog.portalInstanceId, changelog.portalInstanceIds],
	)

	// A workspace on the main portal alone has nothing to choose between.
	if (namedPortals.length === 0) return null

	const handleToggle = async (portalId: string) => {
		const next = togglePortalId(
			selected,
			portalId,
			portals.map((portal) => portal.id),
		)
		if (next.length === 0) {
			toast.info({
				title: "Keep at least one portal",
				description: "An entry with no portal cannot be read by anyone.",
			})
			return
		}

		await mutateOk(
			zero.mutate(
				mutators.changelogs.update({
					id: changelog.id,
					portalInstanceIds: next,
				}),
			),
		)
		analytics.track("update-changelog-portals", {
			id: changelog.id,
			count: next.length,
		})
	}

	return (
		<DropdownMenu
			contentProps={{ align: "end" }}
			trigger={
				<Button variant="ghost" size="sm" Icon={Globe} disabled={!canEdit}>
					{portalSelectionLabel(selected, portals)}
				</Button>
			}
		>
			<div className="flex flex-col gap-0.5">
				<div className="px-2 pb-1 pt-1.5 text-xs font-medium text-secondary">
					Visible on
				</div>
				{portals.map((portal) => {
					const checked = selected.includes(portal.id)
					return (
						<button
							key={portal.id}
							type="button"
							disabled={!canEdit}
							onClick={(event) => {
								event.preventDefault()
								event.stopPropagation()
								void handleToggle(portal.id)
							}}
							className="flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-gray-800"
						>
							<div className="flex min-w-0 flex-1 items-center gap-2">
								{portal.id === MAIN_PORTAL_INSTANCE_ID && (
									<Globe className="h-3.5 w-3.5 flex-none text-tertiary" />
								)}
								<span className="min-w-0 flex-1 truncate" title={portal.name}>
									{portal.name}
								</span>
							</div>
							<motion.span
								initial={false}
								animate={{ opacity: checked ? 1 : 0, scale: checked ? 1 : 0.6 }}
								transition={{ duration: 0.14, ease: [0.16, 1, 0.3, 1] }}
								className="flex flex-none"
							>
								<Check className="h-4 w-4" />
							</motion.span>
						</button>
					)
				})}
			</div>
		</DropdownMenu>
	)
}
